import { isDefined, MatSerNumber, NotificationIdentifier } from '@evorait/pace-core';
import { createSelector, MemoizedSelector } from '@ngrx/store';
import { Answers } from '../../types/answers';
import { CheckpointDependencies, Checkpoints } from '../../types/checkpoints';
import { CHOICE_TYPE } from '../../types/choice';
import { ContentBlock, ContentId } from '../../types/content-block';
import { ContentBlocks } from '../../types/content-blocks';
import { CONTENT_TYPE } from '../../types/content-child';
import { CustomerIdentification } from '../../types/customer-identification';
import {
    CustomerIdentificationInputField,
    SortedCustomerIdentificationInputField,
} from '../../types/customer-identification-input-field';
import { FormParticipantCopies } from '../../types/form-participant-copies';
import { FormsConstants } from '../../types/forms-constants';
import { FUNCTIONAL_ELEMENT_TYPE, FunctionalElement } from '../../types/functional-element';
import { FunctionalElements } from '../../types/functional-elements';
import { isCosimaOption, OptionDependencies } from '../../types/option';
import { RadioOptions } from '../../types/radio-options';
import { isSignature, Signature } from '../../types/signature';
import { DEFICIENCY_SELECTION, MEASUREMENT_SELECTION } from '../../types/system-condition';
import { TypeFilter } from '../../types/type-filter';
import { isAnyFilterActive } from '../../utilities/answer-utilities';
import { FormUtilities } from '../../utilities/form-utilities';
import { StatusUtilities } from '../../utilities/status-utilities';
import { SubmitStatusUtilities } from '../../utilities/submit-status-utilities';
import { FormState } from '../state/form.state';
import { FormsState } from '../state/pace-forms.state';
import { getPaceFormsState } from './pace-forms.selector';
import moment from 'moment';
import { MeasuringEquipmentsControlName } from '../../types/measuring-equipments';
import { Checkpoint } from '../../types/checkpoint';
import { AnswerStatus } from '../../types/answer-status';
import { RadioOption } from '../../types/radio-option';
import { MeasurementEquipmentUtilities } from '../../utilities/measurement-equipment.utilities';
import { FormListUtilities } from '../../utilities/form-list-utilities';
import { NotificationFormListState } from '../state/notification-form-list.state';
import { getFormNotificationLists } from './list.selector';
import SUBMIT_STATUS = FormsConstants.SUBMIT_STATUS;
import FORMTYPE = FormsConstants.FORMTYPE;
import isCosimaForm = FormUtilities.isCosimaForm;

export const getForm = createSelector(getPaceFormsState, (state) => state.form);
export const getFormId = createSelector(getForm, (form) => form.formId);
export const getFormType = createSelector(getForm, (form) => form.type);
export const getFormEscalationEmail = createSelector(getForm, (form) => form.escalationEmail);
export const getFormIsLoading = createSelector(getForm, (form) => form.isLoading);
export const getFormLoadingStatus = createSelector(getForm, (form) => form.loadingStatus);
export const getFormIsSaving = createSelector(getForm, (form) => form.isSaving);
export const getFormLock = createSelector(getForm, (form) => form.lockedBy);
export const getDeviceId = createSelector(getForm, (form) => form.deviceId);
export const getFormDoctype = createSelector(getForm, (form) => form.doctype);
export const getFormParticipantCopies = createSelector(getForm, (form) => form.participantCopies);
export const getFormSelectedParticipantCopyId = createSelector(getForm, (form) => form.selectedParticipantCopyId);
export const getFormMaster = createSelector(
    getForm,
    (form) => form.participantCopies[FormsConstants.MASTER_PARTICIPANT_ID],
);
export const getFormOrgData = createSelector(getForm, (form) => form.orgData);
export const getFormCopyright = createSelector(getForm, (form) => form.copyright);
export const getIsSAFormSubmitted = (
    safFormId: string,
    currentNotificationNo: string,
): MemoizedSelector<FormsState, boolean> =>
    createSelector(getForm, (form) => {
        const isFormUnsaved = form.loadingStatus !== FormsConstants.FORM_LOADING_STATUS.SAVED;
        const isSubmitted = form.participantCopies.master.submittedOn !== '';
        return (
            isSubmitted &&
            isFormUnsaved &&
            form.formId.formId === safFormId &&
            form.notification.qmNum === currentNotificationNo
        );
    });
export const getFormInstructions = createSelector(getForm, (form) => form.instructions);
export const getFormInfoClass = createSelector(getForm, (form) => form.infoClass);
export const getFormHeadings = createSelector(getForm, (form) => form.headings);
export const getFormContent = createSelector(getForm, (form) => form.content);
export const getFormCheckpoints = createSelector(getForm, (form) => form.checkpoints);
export const getAccordionGroupStateFactory = (props: {
    groupId: string;
}): MemoizedSelector<FormsState, boolean, (x: FormState) => boolean> =>
    createSelector(getForm, (form: FormState) => form.accordionGroups[props.groupId]);
export const getMeasuringEquipments = createSelector(getForm, (state) => state.measuringEquipments);

export const getNoMeasurementToolsUsed = createSelector(
    getMeasuringEquipments,
    (state) => state?.noMeasurementToolsUsed,
);

export const getMeasuringEquipmentsCalibratedDateStatus = createSelector(getMeasuringEquipments, (state) => {
    return state?.rows?.some(
        (row) =>
            row.visibility &&
            row.columns.some(
                (column) =>
                    column.columnName === MeasuringEquipmentsControlName.Calibrated &&
                    moment(column.value).isBefore(MeasurementEquipmentUtilities.getDateUsed(row)),
            ),
    );
});

// Selector for retrieving the readOnly status of a form.
// The status is being set when the form is opened in read-only/preview mode.
export const getFormGlobalReadonly = createSelector(
    getForm,
    (form) => form.readOnlyReason !== FormsConstants.READONLY_REASON.NONE,
);

// Selector to know if the participant copy workflow should be enabled for this particular form.
export const getFormSupportParticipantCopies = createSelector(getForm, (form) => form.supportParticipantCopies);

// Selector for retrieving the read-only status of a participant Copy.
// A participant Copy should be read-only if the global read-only mode is active or, the currently selected participant
// form has already been submitted.
export const getFormParticipantCopyReadonly = createSelector(
    getFormGlobalReadonly,
    getForm,
    (readOnly, form) =>
        readOnly ||
        (form.selectedParticipantCopyId !== FormsConstants.MASTER_PARTICIPANT_ID &&
            form.participantCopies[form.selectedParticipantCopyId].submittedOn.length > 0),
);

export const getFormReadonlyReason = createSelector(getForm, (form) => form.readOnlyReason);
export const getFormRadioButtonLabels = createSelector(getForm, (form) => form.radioButtonLabels);
export const getFormCheckpointDependencies = createSelector(getForm, (form) => form.checkpointDependencies);
export const getFormTypeFilters = createSelector(getForm, (form) => form.typeFilters);
export const getFormFilters = createSelector(getForm, (form) => form.formFilters);
export const getFormVisibleTypeFilters = createSelector(
    getFormTypeFilters,
    getFormFilters,
    (typeFilters, formFilters) => {
        const typeFilterArray = Object.values(typeFilters);
        return !isAnyFilterActive(typeFilters, formFilters)
            ? typeFilterArray
            : typeFilterArray.filter((f) => f.visible);
    },
);

export const getIsAnyFilterActive = createSelector(getFormTypeFilters, getFormFilters, (typeFilters, formFilters) => {
    return isAnyFilterActive(typeFilters, formFilters);
});

export const getFormNotification = createSelector(getForm, (form) => form.notification);
export const getFormRecipients = createSelector(getForm, (form) => form.recipients);
export const getTopLevelFormContent = createSelector(getFormContent, (content) =>
    Object.values(content).filter((contentBlock) => contentBlock.parentId === -1),
);
export const getFormSystemCondition = createSelector(getForm, (form) => form.systemCondition);
export const getFormSystemConditionEmployeeSignature = createSelector(
    getFormSystemCondition,
    (systemCondition) => systemCondition?.employeeSignature,
);
export const getFormSystemConditionCustomerSignature = createSelector(
    getFormSystemCondition,
    (systemCondition) => systemCondition?.customerSignature,
);

export const getFormHintsForFilling = createSelector(getForm, (form) => form.hintsForFilling);

export const getFormParticipantsSelectedInParticipantCopiesModal = createSelector(
    getFormParticipantCopies,
    (participantCopies) =>
        Object.values(participantCopies).filter((participantCopy) => participantCopy.selectedInParticipantCopiesModal),
);

export const getFormParticipantsWithoutCopies = createSelector(getFormParticipantCopies, (participantCopies) =>
    Object.values(participantCopies).filter(
        (participantCopy) => participantCopy.id !== FormsConstants.MASTER_PARTICIPANT_ID && !participantCopy.hasCopy,
    ),
);

export const getFormParticipantTitles = createSelector(getForm, (form) => form.participantTitles);
export const getFormEmployeeSignatureFromMaster = createSelector(getForm, (form) => {
    const employeeSignature = FormUtilities.isCosimaForm(form.type)
        ? FUNCTIONAL_ELEMENT_TYPE.EMPLOYEE_SIGNATURE
        : FUNCTIONAL_ELEMENT_TYPE.SIGNOFF_EMPLOYEE;

    return Object.values(form.participantCopies[FormsConstants.MASTER_PARTICIPANT_ID].functionalElements).find(
        (fe) => fe.type === employeeSignature,
    ) as Signature | undefined;
});

// selectors which depend on currently selected participant
export const getFormSelectedParticipantCopy = createSelector(
    getFormParticipantCopies,
    getFormSelectedParticipantCopyId,
    (participantCopies, selectedParticipant) => participantCopies[selectedParticipant],
);

export const getFormSignaturesForCurrentParticipant = createSelector(getFormSelectedParticipantCopy, (copy) =>
    Object.values(copy.functionalElements).filter((f) => isSignature(f)),
);

export const getInstallationForm = createSelector(
    getFormSelectedParticipantCopy,
    getFormType,
    getFormInfoClass,
    (copy, formType, infoClass) => {
        if (copy.id !== FormsConstants.MASTER_PARTICIPANT_ID) {
            return false;
        }

        return FormUtilities.isInstallationReport(formType, infoClass);
    },
);

export const getFormSubmitStatusForCurrentParticipant = createSelector(
    getFormSelectedParticipantCopy,
    getTopLevelFormContent,
    getFormType,
    getMeasuringEquipments,
    getMeasuringEquipmentsCalibratedDateStatus,
    getFormInfoClass,
    (copy, topLevelContent, formType, measuringEquipments, isMeasuringEquipmentsCalibratedDateInvalid, infoClass) => {
        const checkpointStatusArray = topLevelContent
            // visible is only available for cosima forms
            .filter((content) => (isDefined(content.visible) ? content.visible : true))
            .map((content) => copy.answers[content.id])
            .map((answer) => StatusUtilities.getCheckpointStatusFromAnswers(answer));

        let submitStatus = checkpointStatusArray
            // eslint-disable-next-line @typescript-eslint/unbound-method
            .reduce(SubmitStatusUtilities.finalOrInterimReducer, SUBMIT_STATUS.FINAL);

        const isInstallationReport = FormUtilities.isInstallationReport(formType, infoClass);
        if (isInstallationReport && submitStatus === SUBMIT_STATUS.FINAL_NOT_OK) {
            submitStatus = SUBMIT_STATUS.INTERIM_NOT_OK;
        }

        const isServicePartQuestionnaire = FormUtilities.isServicePartQuestionnaire(formType);
        if (isServicePartQuestionnaire && submitStatus === SUBMIT_STATUS.FINAL_NOT_OK) {
            submitStatus = SUBMIT_STATUS.FINAL;
        }

        if (isDefined(measuringEquipments) && measuringEquipments.visibility) {
            if (!measuringEquipments.canSubmitForm) {
                if (submitStatus === SUBMIT_STATUS.FINAL_NOT_OK) {
                    submitStatus = SUBMIT_STATUS.INTERIM_NOT_OK;
                } else if (submitStatus === SUBMIT_STATUS.FINAL) {
                    submitStatus = SUBMIT_STATUS.INTERIM;
                }
            }
            if (isMeasuringEquipmentsCalibratedDateInvalid) {
                submitStatus = SUBMIT_STATUS.INTERIM_NOT_OK;
            }
        }

        return submitStatus;
    },
);

export const getFormDefectServicePartIdentification = createSelector(
    getFormMaster,
    (participantCopy) => participantCopy.defectServicePartIdentification,
);

export const getFormDefectServicePartIdentificationValid = createSelector(
    getFormDefectServicePartIdentification,
    (defectServicePartIdentification) =>
        isDefined(defectServicePartIdentification) &&
        defectServicePartIdentification.materialNo.value.length > 0 &&
        defectServicePartIdentification.serialNo.value.length > 0 &&
        defectServicePartIdentification.orderNo.value.length > 0,
);

export const getQuestionnaireSubmitStatus = createSelector(
    getForm,
    getFormSubmitStatusForCurrentParticipant,
    getFormDefectServicePartIdentificationValid,
    (form, status, defectServicePartIdentificationValid) =>
        FormUtilities.isServicePartQuestionnaire(form.type) &&
        (status === SUBMIT_STATUS.FINAL || status === SUBMIT_STATUS.FINAL_NOT_OK) &&
        defectServicePartIdentificationValid,
);

export const getFormCustomerSignatureForCurrentParticipant = createSelector<
    FormsState,
    [FunctionalElement[]],
    Signature | undefined
>(getFormSignaturesForCurrentParticipant, (signatures: Signature[]): Signature | undefined =>
    signatures.find(
        (fe) =>
            fe.type === FUNCTIONAL_ELEMENT_TYPE.SIGNOFF_CUSTOMER ||
            fe.type === FUNCTIONAL_ELEMENT_TYPE.APPROVER_SIGNATURE,
    ),
);

export const getFormEmployeeSignatureForCurrentParticipant = createSelector<
    FormsState,
    [FunctionalElement[]],
    Signature | undefined
>(getFormSignaturesForCurrentParticipant, (signatures: Signature[]): Signature | undefined =>
    signatures.find(
        (fe) =>
            fe.type === FUNCTIONAL_ELEMENT_TYPE.SIGNOFF_EMPLOYEE ||
            fe.type === FUNCTIONAL_ELEMENT_TYPE.EMPLOYEE_SIGNATURE,
    ),
);

export const getSignaturesValid = createSelector<FormsState, [Signature | undefined, Signature | undefined], boolean>(
    getFormCustomerSignatureForCurrentParticipant,
    getFormEmployeeSignatureForCurrentParticipant,
    (customerSignature: Signature | undefined, employeeSignature: Signature | undefined): boolean => {
        const customerSignatureValid = FormUtilities.isCosimaSignatureValid(customerSignature);
        const employeeSignatureValid = FormUtilities.isCosimaSignatureValid(employeeSignature);

        return customerSignatureValid && employeeSignatureValid;
    },
);

export const getFormUnSubmittedParticipantCopyIds = createSelector(getFormParticipantCopies, (participantCopies) =>
    Object.values(participantCopies)
        .filter(
            (participantCopy) =>
                participantCopy.submittedOn.length === 0 && participantCopy.id !== FormsConstants.MASTER_PARTICIPANT_ID,
        )
        .map((p) => p.id),
);
export const getFormXml = createSelector(getFormSelectedParticipantCopy, (participantCopy) => participantCopy.xml);
export const getFormCustomerIdentification = createSelector(
    getFormSelectedParticipantCopy,
    (participantCopy) => participantCopy.customerIdentification,
);

export const getFormSortedCustomerIdentification = createSelector(
    getFormCustomerIdentification,
    (customerIdentification) =>
        Object.keys(customerIdentification)
            .map((key: keyof CustomerIdentification) => {
                return {
                    ...customerIdentification[key],
                    key,
                } as SortedCustomerIdentificationInputField;
            })
            .sort((a, b) => (a.order > b.order ? 1 : -1)),
);

export const getFormAnswers = createSelector(
    getFormSelectedParticipantCopy,
    (participantCopy) => participantCopy.answers,
);

export const getFormFunctionalElements = createSelector(
    getFormSelectedParticipantCopy,
    (participantCopy) => participantCopy.functionalElements,
);
export const getFormRadioOptions = createSelector(
    getFormSelectedParticipantCopy,
    (participantCopy) => participantCopy.options,
);

export const getFormParticipantCopyModal = createSelector(getForm, (form) => form.participantCopyModal);
export const getCurrentlyEditedParticipant = createSelector(
    getFormParticipantCopies,
    getFormParticipantCopyModal,
    (participantCopies, participantCopyModal) => participantCopies[participantCopyModal.currentlyEditedParticipantId],
);

export const getSystemConditionValidity = createSelector(getForm, (form) => {
    const systemCondition = form.systemCondition;

    if (!isDefined(systemCondition)) {
        return true;
    }

    const deficienciesInvalidValue = isCosimaForm(form.type) ? '' : DEFICIENCY_SELECTION.NO_SELECTION;

    return (
        FormUtilities.isSignatureValid(systemCondition.employeeSignature) &&
        FormUtilities.isSignatureValid(systemCondition.customerSignature) &&
        (!isDefined(systemCondition.measurementSelection) ||
            systemCondition.measurementSelection !== MEASUREMENT_SELECTION.NO_SELECTION) &&
        systemCondition.deficienciesSelection !== deficienciesInvalidValue
    );
});

/*
 * The following selectors are using factories to enable easy creation of memoized selectors based on their props.
 * Memoization means that the selector will cache result for this specific props object and save the last result
 * computation as long as the props are not changed. So if you want optimized access to a specific sub-property of a
 * key/value object, you can use the factory to create your very own selector for quick access.
 */
export const getFormCustomerIdentificationBindingFactory = (props: {
    property: keyof CustomerIdentification;
}): MemoizedSelector<
    FormsState,
    CustomerIdentificationInputField | undefined,
    (x: CustomerIdentification) => CustomerIdentificationInputField | undefined
> =>
    createSelector(
        getFormCustomerIdentification,
        (customerIdentification: CustomerIdentification) => customerIdentification[props.property],
    );

export const getFormContentByIdFactory = (props: {
    id: ContentId;
}): MemoizedSelector<FormsState, ContentBlock, (x: ContentBlocks) => ContentBlock> =>
    createSelector(getFormContent, (content: ContentBlocks) => content[props.id]);

export const getFormCheckpointByIdFactory = (props: {
    id: ContentId;
}): MemoizedSelector<FormsState, Checkpoint, (x: Checkpoints) => Checkpoint> =>
    createSelector(getFormCheckpoints, (checkpoints: Checkpoints) => checkpoints[props.id]);

export const getFormCheckpointByXmlIdFactory = (props: {
    xmlId: string;
}): MemoizedSelector<FormsState, Checkpoint | undefined, (x: Checkpoints) => Checkpoint | undefined> =>
    createSelector(getFormCheckpoints, (checkpoints: Checkpoints) =>
        Object.values(checkpoints).find((c) => c.xmlId === props.xmlId),
    );

export const getFormAnswersByContentIdFactory = (props: {
    id: ContentId;
}): MemoizedSelector<FormsState, AnswerStatus, (x: Answers) => AnswerStatus> =>
    createSelector(getFormAnswers, (answers: Answers) => answers[props.id]);

export const getFormFunctionalElementByIdFactory = <T extends FunctionalElement>(props: {
    id: ContentId;
}): MemoizedSelector<FormsState, T, (x: FunctionalElements) => T> =>
    createSelector(
        getFormFunctionalElements,
        (functionalElements: FunctionalElements) => functionalElements[props.id] as T,
    );

export const getFormRadioOptionsByXmlIdFactory = (props: {
    xmlId: string;
}): MemoizedSelector<FormsState, RadioOption[] | undefined, (x: RadioOptions) => RadioOption[] | undefined> =>
    createSelector(getFormRadioOptions, (options: RadioOptions) => options[props.xmlId]);

export const getCheckpointsToAutofillFromContentFactory = (props: {
    contentId: ContentId;
}): MemoizedSelector<
    FormsState,
    Checkpoint[],
    (
        content: ContentBlocks,
        checkpoints: Checkpoints,
        options: RadioOptions,
        visibleTypeFilters: TypeFilter[],
        functionalElements: FunctionalElements,
        dependencies: CheckpointDependencies,
    ) => Checkpoint[]
> =>
    createSelector(
        getFormContent,
        getFormCheckpoints,
        getFormRadioOptions,
        getFormVisibleTypeFilters,
        getFormFunctionalElements,
        getFormCheckpointDependencies,
        (
            content: ContentBlocks,
            checkpoints: Checkpoints,
            options: RadioOptions,
            visibleTypeFilters: TypeFilter[],
            functionalElements: FunctionalElements,
            dependencies: CheckpointDependencies,
        ) => {
            const contentBlocks = [props.contentId];
            const checkpointsToAutofill: ContentId[] = [];

            while (contentBlocks.length > 0 && Object.values(content).length > 0) {
                // using the non-null assertion operator '!' here because we checked if there would be elements to
                // shift() in the while loop header
                // https://www.typescriptlang.org/docs/handbook/release-notes/typescript-2-0.html#non-null-assertion-operator
                const contentId = contentBlocks.shift();

                if (!isDefined(contentId)) {
                    throw new Error(
                        `FormSelector: getCheckpointsToAutofillFromContentFactory: Unable to get content id '${contentId}'`,
                    );
                }
                const contentBlock = content[contentId];

                checkpointsToAutofill.push(
                    ...contentBlock.children
                        // Autofill only applies to checkpoints. Functional elements have to be filled by hand.
                        .filter((cb) => cb.type === CONTENT_TYPE.CHECKPOINT)
                        .map((cb) => checkpoints[cb.id])
                        // Only select checkpoints that are not excluded from autofill
                        .filter(
                            (checkpoint) =>
                                !FormsConstants.ELEMENT_TYPES_EXCLUDED_FROM_AUTOFILL.includes(checkpoint.elementType),
                        )
                        // Only select checkpoints that are currently visible
                        .filter((checkpoint) =>
                            visibleTypeFilters.some((f) => f.elementType === checkpoint.elementType),
                        )
                        // Autofill only when there was no selection for the checkpoint in the XML
                        .filter((checkpoint) => {
                            const selectedOption = options[checkpoint.xmlId]?.find((o) => o.selected);

                            return !(isDefined(selectedOption) && selectedOption.contentLoadedFromXml);
                        })
                        // Autofill only checkpoints that do not control a signature (this selection always needs to
                        // be given by the user)
                        .filter((checkpoint) => {
                            const dependency = dependencies[checkpoint.xmlId];
                            return (
                                typeof dependency === 'undefined' ||
                                (functionalElements[dependency.functionalElement].type !==
                                    FUNCTIONAL_ELEMENT_TYPE.SIGNOFF_CUSTOMER &&
                                    functionalElements[dependency.functionalElement].type !==
                                        FUNCTIONAL_ELEMENT_TYPE.SIGNOFF_EMPLOYEE &&
                                    functionalElements[dependency.functionalElement].type !==
                                        FUNCTIONAL_ELEMENT_TYPE.SIGNOFF_EMPLOYEE_SYSTEM_CONDITION &&
                                    functionalElements[dependency.functionalElement].type !==
                                        FUNCTIONAL_ELEMENT_TYPE.SIGNOFF_CUSTOMER_SYSTEM_CONDITION)
                            );
                        })
                        .map((checkpoint) => checkpoint.id),
                );

                contentBlocks.push(
                    ...contentBlock.children.filter((cb) => cb.type === CONTENT_TYPE.CONTENT).map((cb) => cb.id),
                );
            }

            return checkpointsToAutofill.map((cp) => checkpoints[cp]);
        },
    );

// only to be used for cosima forms
export const getFunctionalElementsToAutofillFromContentFactory = (props: {
    contentId: ContentId;
}): MemoizedSelector<
    FormsState,
    FunctionalElement[],
    (
        formType: FORMTYPE,
        content: ContentBlocks,
        visibleTypeFilters: TypeFilter[],
        functionalElements: FunctionalElements,
    ) => FunctionalElement[]
> =>
    createSelector(
        getFormType,
        getFormContent,
        getFormVisibleTypeFilters,
        getFormFunctionalElements,
        (
            formType: FORMTYPE,
            content: ContentBlocks,
            visibleTypeFilters: TypeFilter[],
            functionalElements: FunctionalElements,
        ) => {
            if (!isCosimaForm(formType)) {
                return [];
            }

            const contentBlocks = [props.contentId];
            const functionalElementsToAutofill: ContentId[] = [];

            while (contentBlocks.length > 0 && Object.values(content).length > 0) {
                // using the non-null assertion operator '!' here because we checked if there would be elements to
                // shift() in the while loop header
                // https://www.typescriptlang.org/docs/handbook/release-notes/typescript-2-0.html#non-null-assertion-operator
                const contentId = contentBlocks.shift();

                if (!isDefined(contentId)) {
                    throw new Error(
                        `FormSelector: getFunctionalElementsToAutofillFromContentFactory: Unable to get content id '${contentId}'`,
                    );
                }

                const contentBlock = content[contentId];

                functionalElementsToAutofill.push(
                    ...contentBlock.children
                        // Autofill only applies to checkpoints. Functional elements have to be filled by hand.
                        .filter((cb) => cb.type === CONTENT_TYPE.FUNCTIONAL)
                        .map((cb) => functionalElements[cb.id])
                        // Only select checkpoints that are not excluded from autofill
                        .filter((functionalElement) => {
                            const functionalElementType =
                                functionalElement.elementType ?? FormsConstants.ELEMENT_TYPE.UNKNOWN;
                            return !FormsConstants.ELEMENT_TYPES_EXCLUDED_FROM_AUTOFILL.includes(functionalElementType);
                        })
                        // Only select checkpoints that are currently visible
                        .filter((functionalElement) =>
                            visibleTypeFilters.some((f) => f.elementType === functionalElement.elementType),
                        )
                        // Autofill only when there was no selection for the checkpoint in the XML
                        .filter(
                            (functionalElement) =>
                                !(isDefined(functionalElement) && functionalElement.contentLoadedFromXml),
                        )
                        // Autofill only checkpoints that do not control a signature (this selection always needs to
                        // be given by the user)
                        .filter(
                            (functionalElement) =>
                                !(
                                    functionalElement.type === FUNCTIONAL_ELEMENT_TYPE.EMPLOYEE_SIGNATURE ||
                                    functionalElement.type === FUNCTIONAL_ELEMENT_TYPE.APPROVER_SIGNATURE
                                ),
                        )
                        .map((functionalElement) => functionalElement.id),
                );

                contentBlocks.push(
                    ...contentBlock.children.filter((cb) => cb.type === CONTENT_TYPE.CONTENT).map((cb) => cb.id),
                );
            }

            return functionalElementsToAutofill.map((cp) => functionalElements[cp]);
        },
    );

export const getFormOptionDependencies = createSelector(getForm, (form) => form.optionDependencies);

const getDependentFunctionalElements = (
    optionDependencies: OptionDependencies,
    functionalElements: FunctionalElements,
    contentId: ContentId,
): FunctionalElement[] => {
    const deps = optionDependencies[contentId];
    const dependentFunctionalElements: FunctionalElement[] = [];
    if (!isDefined(deps)) {
        throw new Error(
            `FormSelector: getDependentFunctionalElements: Unable to load option dependencies for content id '${contentId}'`,
        );
    }
    deps.functionalElements.forEach((elementContentId) => {
        if (isCosimaOption(functionalElements[elementContentId])) {
            dependentFunctionalElements.push(
                ...getDependentFunctionalElements(optionDependencies, functionalElements, elementContentId),
            );
        }
        dependentFunctionalElements.push(functionalElements[elementContentId]);
    });
    return dependentFunctionalElements;
};

export const getFormOptionDependenciesByIdFactory = (props: {
    contentId: ContentId;
}): MemoizedSelector<
    FormsState,
    {
        checkpointOptions: Array<{
            checkpoint: Checkpoint;
            isSelected: boolean;
        }>;
        functionalElements: FunctionalElement[];
    },
    (
        dependencies: OptionDependencies,
        checkpoints: Checkpoints,
        functionalElements: FunctionalElements,
        options: RadioOptions,
    ) => {
        checkpointOptions: Array<{
            checkpoint: Checkpoint;
            isSelected: boolean;
        }>;
        functionalElements: FunctionalElement[];
    }
> =>
    createSelector(
        getFormOptionDependencies,
        getFormCheckpoints,
        getFormFunctionalElements,
        getFormRadioOptions,
        (
            dependencies: OptionDependencies,
            checkpoints: Checkpoints,
            functionalElements: FunctionalElements,
            options: RadioOptions,
        ) => {
            // the dependencies are empty when the form is being unloaded.
            if (Object.keys(dependencies).length === 0) {
                return {
                    checkpointOptions: [],
                    functionalElements: [],
                };
            }

            const deps = dependencies[props.contentId];
            if (!isDefined(deps)) {
                throw new Error(
                    `FormSelector: getFormOptionDependenciesByIdFactory: Unable to load option dependencies for content id '${props.contentId}'`,
                );
            }

            const affectedCheckpointOptions = deps.checkpoints.map((c) => {
                return {
                    checkpoint: checkpoints[c],
                    isSelected: !!options[checkpoints[c]?.xmlId]?.some((o) => o.selected),
                };
            });
            const affectedFunctionalElements = getDependentFunctionalElements(
                dependencies,
                functionalElements,
                props.contentId,
            );

            return {
                checkpointOptions: affectedCheckpointOptions,
                functionalElements: affectedFunctionalElements,
            };
        },
    );

export const getHasCopyForAttendee = (props: {
    participantId: string;
}): MemoizedSelector<FormsState, boolean, (x: FormParticipantCopies) => boolean> =>
    createSelector(getFormParticipantCopies, (participantCopies: FormParticipantCopies) => {
        const hasParticipantCopy = participantCopies[props.participantId]?.hasCopy;
        return !!hasParticipantCopy;
    });

export const getFormCheckPointDependentFunctionElementByIdFactory = (props: {
    xmlId: string;
}): MemoizedSelector<
    FormsState,
    FunctionalElement | undefined,
    (dependencies: CheckpointDependencies, functionalElements: FunctionalElements) => FunctionalElement | undefined
> =>
    createSelector(
        getFormCheckpointDependencies,
        getFormFunctionalElements,
        (dependencies: CheckpointDependencies, functionalElements: FunctionalElements) => {
            const deps = dependencies[props.xmlId];
            return isDefined(deps) ? functionalElements[deps.functionalElement] : undefined;
        },
    );

export const getOptionPresentForFunctionalElementByIdFactory = (props: {
    contentId: ContentId;
}): MemoizedSelector<
    FormsState,
    FunctionalElement[],
    (optionDependencies: OptionDependencies, functionalElements: FunctionalElements) => FunctionalElement[]
> =>
    createSelector(
        getFormOptionDependencies,
        getFormFunctionalElements,
        (optionDependencies: OptionDependencies, functionalElements: FunctionalElements) => {
            return Object.values(optionDependencies)
                .filter(isDefined)
                .filter((opt) => opt.functionalElements.includes(props.contentId))
                .map((o) => functionalElements[o.option]);
        },
    );

export const getOptionPresentNoSelectedForFunctionalElement = (props: {
    contentId: ContentId;
}): MemoizedSelector<FormsState, boolean, (options: FunctionalElement[]) => boolean> =>
    createSelector(
        getOptionPresentForFunctionalElementByIdFactory({ contentId: props.contentId }),
        (options: FunctionalElement[]) => {
            const optionPresentSelectedNo = options.reduce(
                (acc, curr) => (acc || curr.choices?.choiceNegative?.selected ? true : false),
                false,
            );

            return optionPresentSelectedNo;
        },
    );

export const isCosimaFunctionalElementFromTrainingChecklistReadOnly = (props: {
    id: ContentId;
}): MemoizedSelector<
    FormsState,
    boolean,
    (formType: FormsConstants.FORMTYPE, functionalElement: FunctionalElement) => boolean
> =>
    createSelector(
        getFormType,
        getFormFunctionalElementByIdFactory({ id: props.id }),
        (formType: FormsConstants.FORMTYPE, functionalElement: FunctionalElement) => {
            let readOnly = false;

            if (isDefined(functionalElement) && isDefined(functionalElement.choices)) {
                const selectedChoice = Object.values(functionalElement.choices).find((choice) => choice?.selected);

                readOnly =
                    // In training checklists, functional elements choices that were set to "yes" in previous session are no longer allowed
                    // to be edited.
                    [
                        FormsConstants.FORMTYPE.APPLICATION_TRAINING_REPORT,
                        FormsConstants.FORMTYPE.PRE_TRAINING_CLARIFICATION_REPORT,
                    ].includes(formType) &&
                    functionalElement.contentLoadedFromXml &&
                    isDefined(selectedChoice) &&
                    selectedChoice.type === CHOICE_TYPE.CHOICE_POSITIVE;
            }

            return readOnly;
        },
    );

export const getManualIndividualizationStatus = createSelector(getForm, (form) => form.enableManualIndividualization);

export const getIndividualizationElementStatus = createSelector(getForm, (form) => form.individualization);

export const getRemarksSection = createSelector(
    getFormSelectedParticipantCopy,
    (participantCopy) => participantCopy.remarksSection,
);

export const getAccordionGroups = createSelector(getForm, (form) => form.accordionGroups);

export const isTopicIndividualizable = (props: {
    id: ContentId;
}): MemoizedSelector<
    FormsState,
    boolean,
    (content: ContentBlock, contentBlocks: { [key: number]: ContentBlock }) => boolean
> =>
    createSelector(
        getFormContentByIdFactory({ id: props.id }),
        getFormContent,
        (content: ContentBlock, contentBlocks: { [key: number]: ContentBlock }) => {
            if (content.topicMaterialNumbers?.length === 0) {
                return false;
            }

            const hasChildWithoutMaterialNumbers = content.children.some(
                (child) => contentBlocks[child.id]?.topicMaterialNumbers?.length === 0,
            );
            if (hasChildWithoutMaterialNumbers) {
                return false;
            }

            return true;
        },
    );

export const getTopicMaterialNumbers = (props: {
    id: ContentId;
}): MemoizedSelector<FormsState, string[], (content: ContentBlock) => string[]> =>
    createSelector(getFormContentByIdFactory({ id: props.id }), (content: ContentBlock): string[] =>
        isDefined(content) && isDefined(content.topicMaterialNumbers) ? content.topicMaterialNumbers : [],
    );

export const getTopicEquipmentSets = (props: {
    id: ContentId;
}): MemoizedSelector<
    FormsState,
    MatSerNumber[],
    (
        topicMaterialNumbers: string[],
        formNotification: NotificationIdentifier,
        formLists: NotificationFormListState,
    ) => MatSerNumber[]
> =>
    createSelector(
        getTopicMaterialNumbers({ id: props.id }),
        getFormNotification,
        getFormNotificationLists,
        (
            topicMaterialNumbers: string[],
            notificationIdentifier: NotificationIdentifier,
            formNotificationLists: NotificationFormListState,
        ) => {
            const listForNotification =
                formNotificationLists[FormListUtilities.generateListIdentifierFromNotification(notificationIdentifier)];
            const equipmentSets = listForNotification?.equipmentSet ?? [];
            return equipmentSets.filter(
                (equipmentSet) =>
                    topicMaterialNumbers.findIndex(
                        (topicMaterialNumber) => topicMaterialNumber === equipmentSet.matnr,
                    ) > -1,
            );
        },
    );
